"use client";

import { useRef, useState, useEffect, type FormEvent } from "react";
import {
  Send,
  X,
  PlusCircle,
  Mic,
  Square,
  Sparkles,
  Wand2,
  Smile,
  FileText,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";
import toast from "react-hot-toast";
import WaveSurfer from "wavesurfer.js";
import RecordPlugin from "wavesurfer.js/dist/plugins/record.esm.js";

import { useChatStore } from "../store/useChatStore";
import { useAuthStore } from "../store/useAuthStore";
import AudioPlayer from "./AudioPlayer";
import AISuggestionPanel from "./chat/AISuggestionPanel";
import { API_BASE_URL } from "../lib/api";

const MessageInput = () => {
  const { sendMessage, selectedUser, replyingTo, setReplyingTo } = useChatStore();
  const { authUser } = useAuthStore();
  const isBusinessDeveloper = authUser?.role === "Business Developer";

  const [text, setText] = useState("");
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<any>(null);
  const [checkingAI, setCheckingAI] = useState(false);
  const [aiChecked, setAiChecked] = useState(false);

  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewResult, setReviewResult] = useState<{
    hasRisks: boolean;
    risks: string[];
    observations: any[];
    assessment: string;
  } | null>(null);

  const [imagePreview, setImagePreview] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [sendingAnyway, setSendingAnyway] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [reviewDone, setReviewDone] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const liveWaveformRef = useRef<HTMLDivElement | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview({
        data: reader.result,
        name: file.name,
        size: file.size,
        type: file.type,
      });
      setText("");
      setAiChecked(false);
      setReviewDone(false);
    };
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    if (!isRecording || !liveWaveformRef.current) return;
    const ws = WaveSurfer.create({
      container: liveWaveformRef.current,
      waveColor: "#bae6fd",
      progressColor: "#03488A",
      height: 36,
    });
    const record = ws.registerPlugin(
      RecordPlugin.create({ renderRecordedAudio: false })
    );
    record.startRecording();
    return () => {
      record.stopRecording();
      ws.destroy();
    };
  }, [isRecording]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      const chunks: BlobPart[] = [];
      recorder.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: "audio/webm" });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        setText("");
        setAiChecked(false);
        setReviewDone(false);
      };
      recorder.start();
      setIsRecording(true);
    } catch {
      toast.error("Mic access denied");
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    mediaRecorderRef.current?.stream.getTracks().forEach((t) => t.stop());
    setIsRecording(false);
  };
 

  const runAICheck = async () => {
    if (!text.trim()) return;
    try {
      setCheckingAI(true);
      const res = await fetch(`${API_BASE_URL}/ai/chat-suggest`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ text }),
      });
      const data = await res.json();
      setAiSuggestions(data);
      setAiChecked(true);
    } catch {
      toast.error("AI check failed");
    } finally {
      setCheckingAI(false);
    }
  };

  const handleAIApply = (newText: string) => {
    setText(newText);
    setAiChecked(true);
    setAiSuggestions(null);
  };

  const hasText = text.trim().length > 0;
  const requiresAI = isBusinessDeveloper && hasText;
  const canSend =
    (!requiresAI || aiChecked) && (hasText || imagePreview || audioBlob);

  const handleSendMessage = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSend) return;

    if (isBusinessDeveloper && (imagePreview || audioBlob) && !reviewDone) {
      try {
        setReviewLoading(true);
        const res = await fetch(`${API_BASE_URL}/ai/review-deliverable`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            clientId: selectedUser?._id,
            messageDraft: text,
            file: imagePreview
              ? {
                  data: imagePreview.data,
                  fileName: imagePreview.name,
                  fileSize: imagePreview.size,
                }
              : audioBlob
              ? {
                  data: await new Promise<string>((resolve) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result as string);
                    reader.readAsDataURL(audioBlob);
                  }),
                  fileName: "voice-note.webm",
                  fileSize: audioBlob.size,
                }
              : null,
          }),
        });
        const data = await res.json();
        const normalizedReview = {
          hasRisks: Boolean(data?.hasRisks),
          risks: Array.isArray(data?.analysis?.risks) ? data.analysis.risks : [],
          observations: [
            ...(data?.analysis?.missing || []),
            ...(data?.analysis?.mismatched || []),
          ],
          assessment: data?.assessment || "",
        };
        setReviewResult(normalizedReview);
        setReviewOpen(true);
        setReviewDone(true);
        setReviewLoading(false);
        return;
      } catch {
        toast.error("AI review failed");
        return;
      }
    }

    const payload: any = { text };
    if (imagePreview) {
      payload.file = {
        data: imagePreview.data,
        fileName: imagePreview.name,
        fileSize: imagePreview.size,
      };
    }
    if (audioBlob) {
      const audioBase64 = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(audioBlob);
      });
      payload.file = {
        data: audioBase64,
        fileName: "voice-note.webm",
        fileSize: audioBlob.size,
      };
    }

    await sendMessage(payload);

    setText("");
    setImagePreview(null);
    setAudioBlob(null);
    setAudioUrl(null);
    setAiChecked(false);
    setAiSuggestions(null);
  };

  const brandGradient = "bg-[linear-gradient(135deg,#03488A_0%,#00AAF1_100%)]";

  return (
    <div>
      {replyingTo && (
        <div className="mb-2.5 flex items-center justify-between gap-2 px-3 py-2 bg-surface-container-low border-l-2 border-primary rounded-lg">
          <div className="min-w-0 text-[12px]">
            <span className="font-semibold text-primary">Replying</span>
            <span className="text-on-surface-variant line-clamp-1"> {replyingTo.text || (replyingTo.file ? `[${replyingTo.file.fileName}]` : "")}</span>
          </div>
          <button onClick={() => setReplyingTo(null)} className="text-outline hover:text-on-surface text-lg leading-none shrink-0">×</button>
        </div>
      )}
      <AISuggestionPanel
        loading={checkingAI}
        suggestions={aiSuggestions}
        onApply={handleAIApply}
      />

      {(imagePreview || audioUrl) && (
        <div className="flex items-center gap-2 mb-2.5 flex-wrap">
          {imagePreview && (
            <div className="flex items-center gap-2 bg-slate-50 ring-1 ring-slate-200 px-2.5 py-1.5 rounded-lg">
              {imagePreview.type === "application/pdf" ? (
                <div className="flex items-center gap-2 text-xs">
                  <div className="w-7 h-7 rounded-md bg-white ring-1 ring-slate-200 grid place-items-center">
                    <FileText size={14} className="text-[#03488A]" />
                  </div>
                  <span className="font-semibold text-slate-700 max-w-[160px] truncate">
                    {imagePreview.name}
                  </span>
                </div>
              ) : (
                <>
                  <img
                    src={imagePreview.data}
                    className="w-9 h-9 rounded-md object-cover ring-1 ring-slate-200"
                  />
                  <span className="text-xs font-semibold text-slate-700 max-w-[140px] truncate">
                    {imagePreview.name}
                  </span>
                </>
              )}
              <button
                type="button"
                onClick={() => setImagePreview(null)}
                className="p-0.5 rounded-md text-slate-400 hover:bg-white hover:text-red-500 transition"
              >
                <X size={13} />
              </button>
            </div>
          )}

          {audioUrl && (
            <div className="flex items-center gap-2 bg-slate-50 ring-1 ring-slate-200 px-2.5 py-1.5 rounded-lg">
              <div className="w-[200px]">
                <AudioPlayer src={audioUrl} />
              </div>
              <button
                type="button"
                onClick={() => {
                  setAudioBlob(null);
                  setAudioUrl(null);
                }}
                className="p-0.5 rounded-md text-slate-400 hover:bg-white hover:text-red-500 transition"
              >
                <X size={13} />
              </button>
            </div>
          )}
        </div>
      )}

      {isRecording && (
        <div className="mb-2.5 px-3 py-2 bg-red-50 ring-1 ring-red-200 rounded-lg flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
          <span className="text-[10px] font-bold uppercase tracking-wider text-red-600">
            Recording
          </span>
          <div ref={liveWaveformRef} className="flex-1" />
        </div>
      )}

      {emojiOpen && (
        <div className="mb-2 flex flex-wrap gap-1.5 px-3 py-2 bg-surface-container-low border border-outline-variant/30 rounded-xl">
          {["😀", "😂", "🙂", "😍", "👍", "🙏", "🎉", "🔥", "✅", "❤️", "👀", "💡", "🚀", "😎", "🤝", "📌"].map((e) => (
            <button key={e} type="button" onClick={() => { setText((t) => t + e); setAiChecked(false); }} className="text-[18px] hover:scale-125 transition-transform">{e}</button>
          ))}
        </div>
      )}

      <input type="file" hidden ref={fileInputRef} onChange={handleFileChange} accept="image/*,.pdf" />

      <form
        onSubmit={handleSendMessage}
        className="flex items-end gap-2 bg-surface-container-low rounded-2xl p-2 border border-outline-variant/30 focus-within:border-primary/50 transition-colors"
      >
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="p-2 text-on-surface-variant hover:text-primary transition-colors shrink-0"
          title="Attach file"
        >
          <PlusCircle size={22} />
        </button>

        <textarea
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setAiChecked(false);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSendMessage(e as unknown as FormEvent);
            }
          }}
          rows={1}
          placeholder="Type a message, use '/' for commands, or drop files (PDF, ZIP, Code, Design)..."
          className="flex-1 bg-transparent border-none focus:ring-0 resize-none py-2 text-[14px] text-on-surface placeholder-outline outline-none min-h-[44px] max-h-32 custom-scrollbar"
        />

        <div className="flex items-center gap-1 pr-1 shrink-0">
          {isBusinessDeveloper && (
            <button
              type="button"
              onClick={runAICheck}
              disabled={checkingAI || !hasText}
              className={`p-2 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${aiChecked ? "text-emerald-600" : "text-primary hover:bg-primary-container/20"}`}
              title={checkingAI ? "Checking…" : aiChecked ? "AI checked" : "AI writing tools"}
            >
              {aiChecked ? <Sparkles size={20} /> : <Wand2 size={20} />}
            </button>
          )}

          <button
            type="button"
            onClick={() => setEmojiOpen((o) => !o)}
            className={`p-2 rounded-lg transition-colors ${emojiOpen ? "text-primary bg-primary-container/20" : "text-on-surface-variant hover:text-primary"}`}
            title="Emoji"
          >
            <Smile size={20} />
          </button>

          <button
            type="button"
            onClick={isRecording ? stopRecording : startRecording}
            className={`p-2 rounded-lg transition-colors ${isRecording ? "bg-error-container text-error" : "text-on-surface-variant hover:text-primary"}`}
            title={isRecording ? "Stop recording" : "Record voice note"}
          >
            {isRecording ? <Square size={20} /> : <Mic size={20} />}
          </button>

          <button
            type="submit"
            disabled={!canSend || reviewLoading}
            className={`w-10 h-10 flex items-center justify-center rounded-xl shadow-sm transition-all ${
              canSend && !reviewLoading ? "bg-primary text-on-primary hover:brightness-110" : "bg-surface-container-high text-outline cursor-not-allowed"
            }`}
          >
            {reviewLoading ? (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
            ) : (
              <Send size={18} />
            )}
          </button>
        </div>
      </form>

      {reviewOpen && reviewResult && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-[0px_24px_48px_rgba(25,28,29,0.16)] w-full max-w-md overflow-hidden">

            <div
              className={`px-5 py-4 flex items-center gap-3 ${
                reviewResult.hasRisks
                  ? "bg-gradient-to-r from-red-50 to-amber-50 border-b border-red-100"
                  : "bg-gradient-to-r from-emerald-50 to-sky-50 border-b border-emerald-100"
              }`}
            >
              <div
                className={`w-10 h-10 rounded-xl grid place-items-center shadow-sm ${
                  reviewResult.hasRisks
                    ? "bg-red-100 text-red-600"
                    : "bg-emerald-100 text-emerald-600"
                }`}
              >
                {reviewResult.hasRisks ? (
                  <AlertTriangle size={18} />
                ) : (
                  <CheckCircle2 size={18} />
                )}
              </div>
              <div className="flex-1 leading-tight">
                <h3 className="font-extrabold text-slate-800 text-sm tracking-tight">
                  Review Before Send
                </h3>
                <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                  AI deliverable check
                </p>
              </div>
            </div>

            <div className="px-5 py-4 space-y-3">
              {reviewResult.hasRisks ? (
                <>
                  <ul className="space-y-2">
                    {reviewResult.risks.map((r, i) => (
                      <li
                        key={i}
                        className="text-xs text-red-700 bg-red-50 ring-1 ring-red-200 rounded-lg px-3 py-2 flex gap-2"
                      >
                        <AlertTriangle size={12} className="shrink-0 mt-0.5" />
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                  {reviewResult.assessment && (
                    <p className="text-xs text-slate-600 italic px-1">
                      {reviewResult.assessment}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-sm text-emerald-700 bg-emerald-50 ring-1 ring-emerald-200 rounded-lg px-3 py-2.5 flex items-center gap-2">
                  <CheckCircle2 size={14} />
                  No major issues detected.
                </p>
              )}
            </div>

            <div className="px-5 py-3.5 bg-slate-50 border-t border-slate-200/60 flex justify-end gap-2">
              <button
                onClick={() => setReviewOpen(false)}
                className="px-3.5 py-2 text-xs font-bold uppercase tracking-wider rounded-lg border border-slate-200 text-slate-600 hover:bg-white transition"
              >
                Cancel
              </button>
              <button
                disabled={sendingAnyway}
                onClick={async () => {
                  try {
                    setSendingAnyway(true);
                    setReviewOpen(false);
                    const payload: any = { text };
                    if (imagePreview) {
                      payload.file = {
                        data: imagePreview.data,
                        fileName: imagePreview.name,
                        fileSize: imagePreview.size,
                      };
                    }
                    if (audioBlob) {
                      const audioBase64 = await new Promise<string>((resolve) => {
                        const reader = new FileReader();
                        reader.onloadend = () => resolve(reader.result as string);
                        reader.readAsDataURL(audioBlob);
                      });
                      payload.file = {
                        data: audioBase64,
                        fileName: "voice-note.webm",
                        fileSize: audioBlob.size,
                      };
                    }
                    setText("");
                    setImagePreview(null);
                    setAudioBlob(null);
                    setAudioUrl(null);
                    setAiChecked(false);
                    setReviewDone(false);
                    await sendMessage(payload);
                  } finally {
                    setSendingAnyway(false);
                  }
                }}
                className={`${brandGradient} text-white px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider shadow-sm hover:opacity-95 disabled:opacity-50 transition`}
              >
                {sendingAnyway ? "Sending…" : "Send anyway"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageInput;
